import {useState, Fragment, useMemo} from 'react';
import { data } from "../data/data";
import styles from  './flow.module.scss';
import CountryFlag from '../common/countryFlag/country-flag';
import ProgressBar from '../common/progressBar/progress-bar.module';
import SelectDropdown from '../common/selectDropdown/select-dropdown';
// import IconSort from '../common/icon/icon-sort';
import {useIsMobile} from "../../hooks/mobile";
import {formatNumberWithDot} from "../../hooks/utils";


const options = [
  {label:"Net HNW Inflow", value:"inflow"},
  {label:"Net HNW Outflow", value:"outflow"}
];

const flowColors = {
  forecastInflow: "#405363",
  actualInflow: "#9FA9B1",
  forecastOutflow: "#6EA7D8",
  actualOutflow: "#B3CBDF"
}

const MigrationFlow = ({...params}) => {

  // const {type} = params;
  // console.log(params)
  const [filterBy, setFilterBy] = useState("inflow");

  // separate the data into inflow/outflow
  const flowData = data.filter(row => row.forecastHNWI!== null && row.actualHNWI).map((row) => {
      return {
        "country": row.country,
        "ISO2": row.ISO2,
        "ISO3": row.ISO3,
        "forecastHNWI": row.forecastHNWI,
        "actualHNWI": row.actualHNWI,
        "inflowRank": row.inflowRank,
        "outflowRank": row.outflowRank
      }
  });

  // console.log(flowData);

  let introText = <span>The data represents the countries and territories with net inflows or net outflows of 100 or more HNWIs (namely, the difference between the number of high-net-worth individuals who relocated to and who emigrated away from a particular country during a given year, with all figures rounded to the nearest 100). Notably, nine of the top 10 countries for net inflows of HNWIs in 2023 host formal <a href="https://www.henleyglobal.com/countries" target="_blank" rel="noreferrer"><u>investment migration</u></a> programs and actively encourage foreign direct investment in return for <a href="https://www.henleyglobal.com/residence-investment" target="_blank" rel="noreferrer"><u>residence</u></a> rights.</span>;
 

  const inflowData = useMemo(()=>(flowData.filter(row => row.forecastHNWI > 0 ).sort((a, b) => a.inflowRank - b.inflowRank) ), [flowData]);
  const maxInflowForecastHNWI = useMemo(()=>( Math.max(...inflowData.map(row => Math.abs(row.forecastHNWI))) ), [inflowData]);
  const maxInflowActualHNWI = useMemo(()=>( Math.max(...inflowData.map(row => Math.abs(row.actualHNWI))) ), [inflowData]);
  const maxInflowHNWI = Math.max(maxInflowForecastHNWI, maxInflowActualHNWI);

  const outflowData = useMemo(()=>(flowData.filter(row => row.forecastHNWI <= 0 ).sort((a, b) => a.outflowRank - b.outflowRank) ), [flowData]);
  const maxOutflowForecastHNWI = useMemo(()=>( Math.max(...outflowData.map(row => Math.abs(row.forecastHNWI))) ), [outflowData]);
  const maxOutflowActualHNWI = useMemo(()=>( Math.max(...outflowData.map(row => Math.abs(row.actualHNWI))) ), [outflowData]);
  const maxOutflowHNWI = Math.max(maxOutflowForecastHNWI, maxOutflowActualHNWI);

  const handleOptionSelect = (option) => {
    setFilterBy(option.value);
  };

  const isMobile = useIsMobile();

  return (
      <>
        <p className="mb-5">{introText}</p>
        <div className={`d-md-none ${styles.sortMobileWrapper}`}>
          <div className={styles.sortTitle}>Filter by</div>
          <SelectDropdown options={options} onSelect={handleOptionSelect} />
        </div>
        <div className={`${styles.tableWrapper}`}>
        { ((filterBy === 'outflow' && isMobile === true) || isMobile === false) &&
        <table className={`${styles.table} ${styles.tableNegative} ${styles.wealthChartTable} ${styles.outFlowTable}`}>
          <thead>
          <tr>
            <th className={`${styles.countryHeading}`}></th>
            <th className={`${styles.percentHeading}`} colSpan={2}>
              <div className="flex flex-column">
                <div className={`${styles.legend} ${styles.legendOutflow}`}>
                  Forecast Net HNWI Outflows 2023 <span style={{ backgroundColor: flowColors.forecastOutflow }}></span>
                </div>
                <div className={`${styles.legend} ${styles.legendOutflow}`}>
                  Actual Net HNWI Outflows 2022 <span style={{ backgroundColor: flowColors.actualOutflow }}></span>
                </div>
              </div>
            </th>
            <th className={`${styles.valueHeading}`}></th>
          </tr>
          </thead>
          <tbody>
          {outflowData.map((item, index) => (
              <Fragment key={index}>
                <tr className={`${styles.contentRow}`}>
                  <td className={`${styles.countryColumn}`}>
                    <CountryFlag country={item.country} iso2={item.ISO2} />
                  </td>
                  <td className={`text-right ${styles.valueColumn} ${styles.valueColumnMobile}`}>
                    <div className="d-flex flex-column">
                      <div>{formatNumberWithDot(item.forecastHNWI)}</div>
                      <div>{formatNumberWithDot(item.actualHNWI)}</div>
                    </div>
                  </td>
                  <td className={`text-right ${styles.percentColumn}`}>
                    <div className="d-flex flex-column align-items-end justify-content-center">
                      <ProgressBar percentValue={item.forecastHNWI !== 0 ? (Math.abs(item.forecastHNWI)/maxOutflowHNWI)*100 : 0} rawValue={item.forecastHNWI} barColor={flowColors.forecastOutflow}/>
                      <ProgressBar percentValue={item.actualHNWI !== 0 ? (Math.abs(item.actualHNWI)/maxOutflowHNWI)*100 : 0} rawValue={item.actualHNWI} barColor={flowColors.actualOutflow}/>
                    </div>
                  </td>
                  <td className={`text-right ${styles.valueColumn}`}>
                    <div className="d-flex flex-column">
                      <div>{formatNumberWithDot(item.forecastHNWI)}</div>
                      <div>{formatNumberWithDot(item.actualHNWI)}</div>
                    </div>
                  </td>
                </tr>
              </Fragment>
          ))}
          </tbody>
        </table> }
        { ((filterBy === 'inflow' && isMobile === true) || isMobile === false) &&
        <table className={`${styles.table} ${styles.tablePositive} ${styles.wealthChartTable} ${styles.inFlowTable}`}>
          <thead>
          <tr>
            <th className={`${styles.countryHeading}`}></th>
            <th className={`${styles.percentHeading}`} colSpan={2}>
              <div className="flex flex-column">
                <div className={`${styles.legend} ${styles.legendInflow}`}><span style={{ backgroundColor: flowColors.forecastInflow }}></span> Forecast Net HNWI Inflows 2023</div>
                <div className={`${styles.legend} ${styles.legendInflow}`}><span style={{ backgroundColor: flowColors.actualInflow }}></span> Actual Net HNWI Inflows 2022</div> 
              </div>
            </th>
            <th className={`${styles.valueHeading}`}></th>
          </tr>
          </thead>
          <tbody>
          {inflowData.map((item, index) => (
              <Fragment key={index}>
                <tr className={`${styles.contentRow}`}>
                  <td className={`${styles.countryColumn}`}>
                    <CountryFlag country={item.country} iso2={item.ISO2} />
                  </td>
                  <td className={`text-right ${styles.valueColumn} ${styles.valueColumnMobile}`}>
                    <div className="d-flex flex-column">
                      <div>{formatNumberWithDot(item.forecastHNWI)}</div>
                      <div>{formatNumberWithDot(item.actualHNWI)}</div>
                    </div>
                  </td>
                  <td className={`text-right ${styles.percentColumn}`}>
                    <div className="d-flex flex-column align-items-end justify-content-center">
                      <ProgressBar percentValue={item.forecastHNWI !== 0 ? (Math.abs(item.forecastHNWI)/maxInflowHNWI)*100 : 0} rawValue={item.forecastHNWI} barColor={flowColors.forecastInflow}/>
                      <ProgressBar percentValue={item.actualHNWI !== 0 ? (Math.abs(item.actualHNWI)/maxInflowHNWI)*100 : 0} rawValue={item.actualHNWI}  barColor={flowColors.actualInflow}/>
                    </div>
                    
                  </td>
                  <td className={`text-right ${styles.valueColumn}`}>
                    <div className="d-flex flex-column">
                      <div>{formatNumberWithDot(item.forecastHNWI)}</div>
                      <div>{formatNumberWithDot(item.actualHNWI)}</div>
                    </div>
                  </td>
                </tr>
              </Fragment>
          ))}
          </tbody>
        </table> }
        </div>
        <div className={`${styles.footerNote} mt-5`}>
          <ul>
            <li>Ranked by 2023 forecast net inflows/outflows.</li>
            <li>HNWI figures rounded to nearest 100.</li>
            <li>‘Millionaires’ or ‘high-net-worth individuals’ or ‘HNWIs’ refer to individuals with investable wealth of USD 1 million or more.</li>
            <li>Countries/territories with a net HNWI inflow/outflow of less than 100 are excluded.</li>
            <li>The 2023 migration forecasts are based on year-to-date HNWI movements to June 2023.</li>
          </ul>
          <p>Source: New World Wealth</p>
        </div>
      </>
  )
}

export default MigrationFlow;